import { PdfLayout } from '../PdfLayout';
import { Play, GitBranch, Zap, Bell, ClipboardList, Split } from 'lucide-react';

const benefits = [
  {
    icon: Play,
    title: 'Event Triggers',
    text: 'Start a run when a tag changes or crosses a threshold, a device goes online or offline, on an interval, cron or time of day, at startup, when a user logs in, or when a network address goes up or down.',
  },
  {
    icon: GitBranch,
    title: 'Visual Flow Builder',
    text: 'Add nodes from a palette and wire them together on a canvas — triggers, logic, timers and counters, input/output and communication.',
  },
  {
    icon: Zap,
    title: 'Branching and Data',
    text: 'If/else conditions, value, string and date comparisons, math, loops and JavaScript expressions, with {{variables}} carrying values from node to node.',
  },
  {
    icon: Split,
    title: 'Parallel Branches and Machine Timers',
    text: 'Fork runs branches at the same time and Join gathers them again. On-Delay waits until a condition has held for a set time, and Run Hours totals a machine\'s running time across restarts.',
  },
  {
    icon: Bell,
    title: 'Built-In Actions',
    text: 'Read and write tags, insert and update Data Table rows, send email, call a URL, publish to MQTT, run scripts and other workflows, and start or stop ladder tasks.',
  },
  {
    icon: ClipboardList,
    title: 'Run History',
    text: 'Every run records its trigger, times and a step-by-step log, and the editor draws the path it took on the canvas — live while it runs. A failed run sets a status tag you can alarm on.',
  },
];

export function Workflows() {
  return (
    <PdfLayout
      category="Automation"
      headline="Automate Any Process with"
      headlineHighlight="Workflows"
      description="Wire triggers, conditions and actions together on a visual canvas. Email a supervisor at shift change, post results to an MES, or log a reading to a table — without writing code."
      accentColor="#7c3aed"
      benefits={benefits}
    >
      <WorkflowMockup />
    </PdfLayout>
  );
}

function WorkflowMockup() {
  return (
    <div className="glass-panel p-4 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Workflow Builder</span>
      </div>

      <svg viewBox="0 0 240 210" className="w-full">
        {/* Trigger node */}
        <rect x="80" y="8" width="80" height="32" rx="6" fill="#7c3aed" fillOpacity="0.1" stroke="#7c3aed" strokeWidth="1.5" />
        <text x="120" y="20" textAnchor="middle" fontSize="7" fill="#7c3aed" fontWeight="600">TAG CHANGE</text>
        <text x="120" y="30" textAnchor="middle" fontSize="6" fill="#64748b">Oven 3 Temp</text>

        {/* Arrow down */}
        <line x1="120" y1="40" x2="120" y2="58" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="116,55 120,62 124,55" fill="#94a3b8" />

        {/* Condition node (diamond) */}
        <polygon points="120,65 155,85 120,105 85,85" fill="#06b6d4" fillOpacity="0.1" stroke="#06b6d4" strokeWidth="1.5" />
        <text x="120" y="83" textAnchor="middle" fontSize="7" fill="#06b6d4" fontWeight="600">Temp</text>
        <text x="120" y="92" textAnchor="middle" fontSize="6" fill="#06b6d4">&gt; 200°F?</text>

        {/* Yes branch (left) */}
        <line x1="85" y1="85" x2="45" y2="85" stroke="#94a3b8" strokeWidth="1" />
        <line x1="45" y1="85" x2="45" y2="128" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="41,125 45,132 49,125" fill="#94a3b8" />
        <text x="60" y="80" fontSize="6" fill="#16a34a" fontWeight="600">YES</text>

        {/* No branch (right) */}
        <line x1="155" y1="85" x2="195" y2="85" stroke="#94a3b8" strokeWidth="1" />
        <line x1="195" y1="85" x2="195" y2="128" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="191,125 195,132 199,125" fill="#94a3b8" />
        <text x="168" y="80" fontSize="6" fill="#dc2626" fontWeight="600">NO</text>

        {/* Action: Send Alert */}
        <rect x="10" y="135" width="70" height="30" rx="6" fill="#dc2626" fillOpacity="0.1" stroke="#dc2626" strokeWidth="1.5" />
        <text x="45" y="148" textAnchor="middle" fontSize="7" fill="#dc2626" fontWeight="600">Send Email</text>
        <text x="45" y="157" textAnchor="middle" fontSize="6" fill="#64748b">Maintenance team</text>

        {/* Action: Log Data */}
        <rect x="160" y="135" width="70" height="30" rx="6" fill="#16a34a" fillOpacity="0.1" stroke="#16a34a" strokeWidth="1.5" />
        <text x="195" y="148" textAnchor="middle" fontSize="7" fill="#16a34a" fontWeight="600">Write DB</text>
        <text x="195" y="157" textAnchor="middle" fontSize="6" fill="#64748b">Insert row</text>

        {/* Both converge */}
        <line x1="45" y1="165" x2="45" y2="180" stroke="#94a3b8" strokeWidth="1" />
        <line x1="195" y1="165" x2="195" y2="180" stroke="#94a3b8" strokeWidth="1" />
        <line x1="45" y1="180" x2="195" y2="180" stroke="#94a3b8" strokeWidth="1" />
        <line x1="120" y1="180" x2="120" y2="192" stroke="#94a3b8" strokeWidth="1" />
        <polygon points="116,189 120,196 124,189" fill="#94a3b8" />

        {/* End node */}
        <circle cx="120" cy="202" r="6" fill="#7c3aed" fillOpacity="0.15" stroke="#7c3aed" strokeWidth="1.5" />
        <circle cx="120" cy="202" r="3" fill="#7c3aed" />
      </svg>

      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[8px] text-text-muted">4 Nodes · 2 Actions</span>
        <span className="text-[8px] text-status-running font-medium">● Enabled</span>
      </div>
    </div>
  );
}
